/**
 * 店家結帳時區常數定義
 *
 * 此檔案定義了 27 個全球時區，用於店家每日結帳時間設定。
 * 時區範圍從 UTC-12 到 UTC+14，涵蓋全球主要營運地點。
 *
 * 格式說明：
 * - offset: UTC 偏移量（-12 到 +14）
 * - value: IANA 標準時區識別碼（用於 API 傳輸和儲存）
 * - label: 顯示名稱（UTC 混合格式：`(UTC+8) Asia/Taipei`）
 *
 * @module settlement-timezones
 */

/**
 * 27 個全球時區常數
 * 使用 `as const` 斷言確保不可變性和型別推導
 */
export const SETTLEMENT_TIMEZONES = [
  { offset: -12, value: 'Etc/GMT+12', label: '(UTC-12) Etc/GMT+12' },
  { offset: -11, value: 'Pacific/Samoa', label: '(UTC-11) Pacific/Samoa' },
  { offset: -10, value: 'Pacific/Honolulu', label: '(UTC-10) Pacific/Honolulu' },
  { offset: -9, value: 'America/Anchorage', label: '(UTC-9) America/Anchorage' },
  { offset: -8, value: 'America/Los_Angeles', label: '(UTC-8) America/Los_Angeles' },
  { offset: -7, value: 'America/Denver', label: '(UTC-7) America/Denver' },
  { offset: -6, value: 'America/Chicago', label: '(UTC-6) America/Chicago' },
  { offset: -5, value: 'America/New_York', label: '(UTC-5) America/New_York' },
  { offset: -4, value: 'America/Caracas', label: '(UTC-4) America/Caracas' },
  { offset: -3, value: 'America/Sao_Paulo', label: '(UTC-3) America/Sao_Paulo' },
  { offset: -2, value: 'Atlantic/South_Georgia', label: '(UTC-2) Atlantic/South_Georgia' },
  { offset: -1, value: 'Atlantic/Azores', label: '(UTC-1) Atlantic/Azores' },
  { offset: 0, value: 'Europe/London', label: '(UTC+0) Europe/London' },
  { offset: 1, value: 'Europe/Paris', label: '(UTC+1) Europe/Paris' },
  { offset: 2, value: 'Europe/Athens', label: '(UTC+2) Europe/Athens' },
  { offset: 3, value: 'Europe/Moscow', label: '(UTC+3) Europe/Moscow' },
  { offset: 4, value: 'Asia/Dubai', label: '(UTC+4) Asia/Dubai' },
  { offset: 5, value: 'Asia/Karachi', label: '(UTC+5) Asia/Karachi' },
  { offset: 6, value: 'Asia/Dhaka', label: '(UTC+6) Asia/Dhaka' },
  { offset: 7, value: 'Asia/Bangkok', label: '(UTC+7) Asia/Bangkok' },
  { offset: 8, value: 'Asia/Taipei', label: '(UTC+8) Asia/Taipei' },
  { offset: 9, value: 'Asia/Tokyo', label: '(UTC+9) Asia/Tokyo' },
  { offset: 10, value: 'Australia/Sydney', label: '(UTC+10) Australia/Sydney' },
  { offset: 11, value: 'Pacific/Noumea', label: '(UTC+11) Pacific/Noumea' },
  { offset: 12, value: 'Pacific/Auckland', label: '(UTC+12) Pacific/Auckland' },
  { offset: 13, value: 'Pacific/Tongatapu', label: '(UTC+13) Pacific/Tongatapu' },
  { offset: 14, value: 'Pacific/Kiritimati', label: '(UTC+14) Pacific/Kiritimati' }
] as const

/**
 * 時區偏移量型別（字面量型別，編譯時嚴格檢查）
 *
 * 只接受 -12 到 +14 之間的整數值，其他值會在編譯時報錯。
 *
 * @example
 * ```typescript
 * const validOffset: TimeZoneOffset = 8 // ✓ 通過
 * const invalidOffset: TimeZoneOffset = 100 // ✗ 編譯錯誤
 * ```
 */
export type TimeZoneOffset = (typeof SETTLEMENT_TIMEZONES)[number]['offset']

/**
 * IANA 標準時區識別碼型別
 *
 * 用於 API 傳輸和資料儲存，符合 IANA 時區資料庫標準。
 *
 * @example
 * ```typescript
 * const timezone: SettlementTimezone = 'Asia/Taipei' // ✓ 通過
 * const invalid: SettlementTimezone = 'Invalid/Zone' // ✗ 編譯錯誤
 * ```
 */
export type SettlementTimezone = (typeof SETTLEMENT_TIMEZONES)[number]['value']

/**
 * 時區資料項目型別
 */
export type SettlementTimezoneItem = (typeof SETTLEMENT_TIMEZONES)[number]
